import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bot, Plus, Trash2, Edit3, Play, Loader2, AlertCircle, ChevronLeft,
  X, Sparkles, Target, DollarSign, Mail, Settings as SettingsIcon,
  Inbox, Send, Clock, RotateCcw, Check, MessageSquare,
} from 'lucide-react';

const COUNTRIES = [
  'United Kingdom', 'United States', 'Germany', 'France', 'Spain', 'Italy',
  'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Belgium',
  'Austria', 'Switzerland', 'Ireland', 'Portugal', 'Greece', 'Czech Republic',
  'Poland', 'Romania', 'Hungary', 'Canada', 'Australia', 'New Zealand',
  'Brazil', 'Mexico', 'Argentina', 'Chile', 'Colombia', 'Peru',
  'India', 'Japan', 'South Korea', 'Turkey', 'South Africa', 'Russia',
];

const VERTICALS = [
  { value: 'casino', label: 'Casino' },
  { value: 'sports betting', label: 'Sports Betting' },
  { value: 'poker', label: 'Poker' },
  { value: 'lottery', label: 'Lottery' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'sweepstake', label: 'Sweepstake' },
];

// Every agent call runs on Claude Haiku 5.5 now, whatever is stored here (the
// backend routes them all through services/claudeClient.js). The legacy gpt-*
// values stay accepted server-side so agents saved before the switch still
// load, but they are no longer offered.
const MODELS = [
  { value: 'claude-haiku-5-5', label: 'Claude Haiku 5.5 (default, fast)' },
];

const CURRENCIES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR',
  'BRL', 'MXN', 'JPY', 'CHF', 'SEK', 'NOK', 'DKK', 'ZAR',
];

const API = process.env.REACT_APP_API_URL || '';
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
});

// ────────────────────────────────────────────────────────────────────
// Default empty configs for each rev/cpa type
// ────────────────────────────────────────────────────────────────────
const DEFAULT_REV = {
  flat: { minPercent: '', maxPercent: '' },
  rev_tier: [{ ngrMin: '', ngrMax: '', activeMin: '', activeMax: '', percent: '' }],
  ftd_tier: [{ ftdMin: '', ftdMax: '', percent: '' }],
};

const DEFAULT_CPA = {
  simple: { registration: '', ftd: '', minDeposit: '', minNetRevenue: '', minWager: '', fromDate: 'registration' },
  ftd_tier: {
    playerRestrictions: { minDeposit: '', minNetRevenue: '', minWager: '' },
    accountTiers: [{ ftdMin: '', ftdMax: '', cpaCommission: '' }],
  },
  deposit_tier: {
    accountTiers: [{ minActivePlayers: '', minFtds: '' }],
    playerRestrictions: [{ minDeposit: '', maxDeposit: '', cpaCommission: '' }],
  },
};

// Hoisted out of AgentForm — declaring components inside the parent's render
// causes React to unmount + remount their children on every keystroke (which
// makes inputs lose focus after each character).
const INPUT_CLS = 'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500';

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 p-5 mb-4 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
        <Icon size={18} />
      </div>
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 dark:text-slate-400">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

// ────────────────────────────────────────────────────────────────────
// Form component — collapsible sections
// ────────────────────────────────────────────────────────────────────
const AgentForm = ({ existing, brands, onCancel, onSaved }) => {
  const [name, setName] = useState(existing?.name || '');
  const [model, setModel] = useState(existing?.model || 'claude-haiku-5-5');
  const [temperature, setTemperature] = useState(existing?.temperature ?? 0.3);
  const [brandId, setBrandId] = useState(existing?.brand_id || '');
  const [verticals, setVerticals] = useState(existing?.verticals || []);
  const [minPosition, setMinPosition] = useState(existing?.min_position ?? '');
  const [maxPosition, setMaxPosition] = useState(existing?.max_position ?? '');

  const [revType, setRevType] = useState(existing?.rev_share_type || '');
  const [revConfig, setRevConfig] = useState(
    existing?.rev_share_config ||
    (existing?.rev_share_type ? null : DEFAULT_REV.flat)
  );

  const [cpaType, setCpaType] = useState(existing?.cpa_type || '');
  const [cpaConfig, setCpaConfig] = useState(
    existing?.cpa_config ||
    (existing?.cpa_type ? null : null)
  );

  const [sourceMode, setSourceMode] = useState(existing?.source_mode || 'auto');
  const [affiliateCount, setAffiliateCount] = useState(existing?.affiliate_count ?? 10);
  const [manualEntries, setManualEntries] = useState(() => {
    if (Array.isArray(existing?.manual_entries) && existing.manual_entries.length) {
      return existing.manual_entries.map(e => ({ email: e.email || '', domain: e.domain || '' }));
    }
    if (Array.isArray(existing?.manual_emails)) {
      return existing.manual_emails.map(email => ({ email, domain: '' }));
    }
    return [];
  });
  const [emailInput, setEmailInput] = useState('');
  const [domainInput, setDomainInput] = useState('');

  const [senderEmail, setSenderEmail] = useState(existing?.sender_email || '');
  const [currency, setCurrency] = useState(existing?.currency || 'USD');
  const [license, setLicense] = useState(existing?.license || '');
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [smtpLoading, setSmtpLoading] = useState(true);
  const [smtpError, setSmtpError] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load connected SMTP accounts so the user can pick which mailbox the agent
  // will send + receive through. Track loading + error state separately so we
  // don't flash the "no SMTP connected" warning during the fetch window.
  const loadSmtp = useCallback(async () => {
    setSmtpLoading(true);
    setSmtpError('');
    try {
      const r = await fetch(`${API}/api/senderEmail/list`, { credentials: 'include', headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'failed to load email accounts');
      const smtp = (d.data || []).filter(a => a.source === 'smtp');
      setSmtpAccounts(smtp);
      if (!existing?.sender_email) {
        const primary = smtp.find(a => a.is_primary) || smtp[0];
        if (primary) setSenderEmail(primary.email);
      }
    } catch (err) {
      setSmtpError(err.message || 'failed to load email accounts');
    } finally {
      setSmtpLoading(false);
    }
  }, [existing]);

  useEffect(() => { loadSmtp(); }, [loadSmtp]);

  // ── Vertical multi-select ──
  const toggleVertical = (v) => {
    setVerticals(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };

  // ── Rev share type change ──
  const changeRevType = (t) => {
    setRevType(t);
    setRevConfig(t ? DEFAULT_REV[t] : null);
  };
  const changeCpaType = (t) => {
    setCpaType(t);
    setCpaConfig(t ? DEFAULT_CPA[t] : null);
  };

  // ── Manual entries: one row = { email, domain }. Domain optional but
  // recommended (the AI uses it as the affiliate's website). Without a domain
  // the outreach falls back to a no-site-data prompt.
  const normalizeDomain = (d) => {
    const v = (d || '').trim().toLowerCase();
    if (!v) return '';
    return v.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  };
  const addEntry = () => {
    const email = emailInput.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const domain = normalizeDomain(domainInput);
    if (manualEntries.some(e => e.email === email)) {
      setEmailInput(''); setDomainInput('');
      return;
    }
    setManualEntries(prev => [...prev, { email, domain }]);
    setEmailInput(''); setDomainInput('');
  };
  const removeEntry = (email) => setManualEntries(prev => prev.filter(e => e.email !== email));

  // ── Submit ──
  const submit = async () => {
    setError('');
    if (!name.trim()) return setError('Agent name is required.');
    if (!brandId) return setError('Select a brand.');
    if (verticals.length === 0) return setError('Select at least one vertical.');
    if (!revType && !cpaType) return setError('Configure at least one of Revenue Share or CPA.');
    if (sourceMode === 'auto' && (!affiliateCount || affiliateCount < 1)) return setError('Affiliate count must be ≥ 1.');
    if (sourceMode === 'manual' && manualEntries.length === 0) return setError('Add at least one email.');

    const payload = {
      name: name.trim(),
      model,
      temperature: parseFloat(temperature),
      brand_id: parseInt(brandId),
      verticals,
      min_position: minPosition === '' ? null : parseInt(minPosition),
      max_position: maxPosition === '' ? null : parseInt(maxPosition),
      rev_share_type: revType || null,
      rev_share_config: revType ? revConfig : null,
      cpa_type: cpaType || null,
      cpa_config: cpaType ? cpaConfig : null,
      source_mode: sourceMode,
      affiliate_count: sourceMode === 'auto' ? parseInt(affiliateCount) : null,
      manual_entries: sourceMode === 'manual' ? manualEntries : null,
      manual_emails: sourceMode === 'manual' ? manualEntries.map(e => e.email) : null,
      sender_email: senderEmail || null,
      currency,
      license: license.trim() || null,
    };

    setSaving(true);
    try {
      const url = existing ? `${API}/api/affiliate-agents/${existing.id}` : `${API}/api/affiliate-agents`;
      const method = existing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, credentials: 'include', headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await r.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      onSaved(data.agent);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = INPUT_CLS;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onCancel} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-400">
          <ChevronLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">
          {existing ? 'Edit Agent' : 'New Affiliate Agent'}
        </h1>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Identity */}
      <Section icon={Sparkles} title="Identity" subtitle="Name, model, and how creative the agent should be">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Agent Name" required>
            <input className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Casino UK Outreach" />
          </Field>
          <Field label="Model" required>
            <select className={inputCls} value={model} onChange={e => setModel(e.target.value)}>
              {MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>
          <Field label="Send & Receive Email Account">
            {smtpLoading ? (
              <div className="text-xs text-gray-500 dark:text-slate-400 px-3 py-2 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Loading email accounts…
              </div>
            ) : smtpError ? (
              <div className="text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                <span>Couldn't load email accounts: {smtpError}</span>
                <button type="button" onClick={loadSmtp} className="px-2 py-1 rounded bg-red-100 dark:bg-red-900/40 hover:bg-red-200 text-xs">Retry</button>
              </div>
            ) : smtpAccounts.length === 0 ? (
              <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                <span>No SMTP account connected. <a href="/email-settings" className="underline">Connect one</a> so the agent can send and read replies.</span>
                <button type="button" onClick={loadSmtp} className="px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 text-xs">Refresh</button>
              </div>
            ) : (
              <select className={inputCls} value={senderEmail} onChange={e => setSenderEmail(e.target.value)}>
                <option value="">Use my primary at run time</option>
                {smtpAccounts.map(a => (
                  <option key={a.id} value={a.email}>
                    {a.email}{a.is_primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Brand Currency" required>
            <select className={inputCls} value={currency} onChange={e => setCurrency(e.target.value)}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Brand License">
            <input
              className={inputCls}
              value={license}
              onChange={e => setLicense(e.target.value)}
              placeholder="e.g. UKGC, MGA, UKGC + MGA, Curacao"
            />
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Used in the first-touch outreach as the brand's trust signal.</p>
          </Field>
          <div className="md:col-span-2">
            <Field label={`Temperature: ${parseFloat(temperature).toFixed(1)}`}>
              <input
                type="range" min={0} max={1} step={0.1}
                value={temperature}
                onChange={e => setTemperature(parseFloat(e.target.value))}
                className="w-full accent-emerald-600"
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mt-1">
                <span>0 — focused</span>
                <span>0.5 — balanced</span>
                <span>1 — creative</span>
              </div>
            </Field>
          </div>
        </div>
      </Section>

      {/* Targeting */}
      <Section icon={Target} title="Targeting" subtitle="Brand and verticals to pitch">
        <div className="grid grid-cols-1 gap-4">
          <Field label="Brand" required>
            <select className={inputCls} value={brandId} onChange={e => setBrandId(e.target.value)}>
              <option value="">— select a brand —</option>
              {brands.map(b => <option key={b.id} value={b.id}>{b.brand_name}</option>)}
            </select>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Only your primary brands are listed (competitor + gap brands excluded).</p>
          </Field>
          <Field label="Verticals (multi-select)" required>
            <div className="flex flex-wrap gap-2">
              {VERTICALS.map(v => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => toggleVertical(v.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                    verticals.includes(v.value)
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Min Listing Position">
              <input type="number" min="1" className={inputCls} value={minPosition} onChange={e => setMinPosition(e.target.value)} placeholder="e.g. 1" />
            </Field>
            <Field label="Max Listing Position">
              <input type="number" min="1" className={inputCls} value={maxPosition} onChange={e => setMaxPosition(e.target.value)} placeholder="e.g. 5" />
            </Field>
          </div>
        </div>
      </Section>

      {/* Revenue Share */}
      <Section icon={DollarSign} title="Revenue Share" subtitle="Pick one structure (or leave blank for CPA-only)">
        <div className="grid grid-cols-1 gap-4">
          <Field label="Type">
            <select className={inputCls} value={revType} onChange={e => changeRevType(e.target.value)}>
              <option value="">— none —</option>
              <option value="flat">Flat Revenue Share</option>
              <option value="rev_tier">Rev-Based Tier Structure (NGR + Active Players)</option>
              <option value="ftd_tier">FTD-Based Tier Structure</option>
            </select>
          </Field>

          {revType === 'flat' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Min %" required>
                <input type="number" min="0" max="100" step="0.5" className={inputCls}
                  value={revConfig?.minPercent ?? ''}
                  onChange={e => setRevConfig({ ...revConfig, minPercent: e.target.value })} placeholder="30" />
              </Field>
              <Field label="Max %" required>
                <input type="number" min="0" max="100" step="0.5" className={inputCls}
                  value={revConfig?.maxPercent ?? ''}
                  onChange={e => setRevConfig({ ...revConfig, maxPercent: e.target.value })} placeholder="50" />
              </Field>
              <p className="col-span-2 text-xs text-gray-500 dark:text-slate-400">
                Agent opens at min and walks up toward max only when the affiliate pushes back.
              </p>
            </div>
          )}

          {revType === 'rev_tier' && (
            <TierEditor
              tiers={revConfig || []}
              setTiers={setRevConfig}
              fields={[
                { key: 'ngrMin', label: 'NGR Min' },
                { key: 'ngrMax', label: 'NGR Max' },
                { key: 'activeMin', label: 'Active Players Min' },
                { key: 'activeMax', label: 'Active Players Max' },
                { key: 'percent', label: 'Percentage %' },
              ]}
              emptyTemplate={{ ngrMin: '', ngrMax: '', activeMin: '', activeMax: '', percent: '' }}
            />
          )}

          {revType === 'ftd_tier' && (
            <TierEditor
              tiers={revConfig || []}
              setTiers={setRevConfig}
              fields={[
                { key: 'ftdMin', label: 'FTDs Min' },
                { key: 'ftdMax', label: 'FTDs Max' },
                { key: 'percent', label: 'Percentage %' },
              ]}
              emptyTemplate={{ ftdMin: '', ftdMax: '', percent: '' }}
            />
          )}
        </div>
      </Section>

      {/* CPA */}
      <Section icon={DollarSign} title="CPA" subtitle="Pick one model (or leave blank for RevShare-only)">
        <div className="grid grid-cols-1 gap-4">
          <Field label="Type">
            <select className={inputCls} value={cpaType} onChange={e => changeCpaType(e.target.value)}>
              <option value="">— none —</option>
              <option value="simple">Simple CPA</option>
              <option value="ftd_tier">FTD-Based Tier Structure</option>
              <option value="deposit_tier">Deposit-Based Tier Structure</option>
            </select>
          </Field>

          {cpaType === 'simple' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Registration ($)"><input type="number" className={inputCls} value={cpaConfig?.registration ?? ''} onChange={e => setCpaConfig({ ...cpaConfig, registration: e.target.value })} /></Field>
              <Field label="FTD ($)"><input type="number" className={inputCls} value={cpaConfig?.ftd ?? ''} onChange={e => setCpaConfig({ ...cpaConfig, ftd: e.target.value })} /></Field>
              <Field label="Min Deposit"><input type="number" className={inputCls} value={cpaConfig?.minDeposit ?? ''} onChange={e => setCpaConfig({ ...cpaConfig, minDeposit: e.target.value })} /></Field>
              <Field label="Min Net Revenue"><input type="number" className={inputCls} value={cpaConfig?.minNetRevenue ?? ''} onChange={e => setCpaConfig({ ...cpaConfig, minNetRevenue: e.target.value })} /></Field>
              <Field label="Min Wager"><input type="number" className={inputCls} value={cpaConfig?.minWager ?? ''} onChange={e => setCpaConfig({ ...cpaConfig, minWager: e.target.value })} /></Field>
              <Field label="From Which Date">
                <select className={inputCls} value={cpaConfig?.fromDate ?? 'registration'} onChange={e => setCpaConfig({ ...cpaConfig, fromDate: e.target.value })}>
                  <option value="registration">Registration date</option>
                  <option value="ftd">FTD date</option>
                </select>
              </Field>
            </div>
          )}

          {cpaType === 'ftd_tier' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-2 mt-2">Player Level Restrictions</h4>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Field label="Min Deposit"><input type="number" className={inputCls} value={cpaConfig?.playerRestrictions?.minDeposit ?? ''} onChange={e => setCpaConfig({ ...cpaConfig, playerRestrictions: { ...cpaConfig.playerRestrictions, minDeposit: e.target.value } })} /></Field>
                <Field label="Min Net Revenue"><input type="number" className={inputCls} value={cpaConfig?.playerRestrictions?.minNetRevenue ?? ''} onChange={e => setCpaConfig({ ...cpaConfig, playerRestrictions: { ...cpaConfig.playerRestrictions, minNetRevenue: e.target.value } })} /></Field>
                <Field label="Min Wager"><input type="number" className={inputCls} value={cpaConfig?.playerRestrictions?.minWager ?? ''} onChange={e => setCpaConfig({ ...cpaConfig, playerRestrictions: { ...cpaConfig.playerRestrictions, minWager: e.target.value } })} /></Field>
              </div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-2">Affiliate Account Level Tiers</h4>
              <TierEditor
                tiers={cpaConfig?.accountTiers || []}
                setTiers={(t) => setCpaConfig({ ...cpaConfig, accountTiers: t })}
                fields={[
                  { key: 'ftdMin', label: 'Min FTDs' },
                  { key: 'ftdMax', label: 'Max FTDs' },
                  { key: 'cpaCommission', label: 'CPA Commission' },
                ]}
                emptyTemplate={{ ftdMin: '', ftdMax: '', cpaCommission: '' }}
              />
            </div>
          )}

          {cpaType === 'deposit_tier' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-2 mt-2">Affiliate Account Level Tiers</h4>
              <TierEditor
                tiers={cpaConfig?.accountTiers || []}
                setTiers={(t) => setCpaConfig({ ...cpaConfig, accountTiers: t })}
                fields={[
                  { key: 'minActivePlayers', label: 'Min Active Players' },
                  { key: 'minFtds', label: 'Min FTDs' },
                ]}
                emptyTemplate={{ minActivePlayers: '', minFtds: '' }}
              />
              <h4 className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-2 mt-4">Player Level Restrictions</h4>
              <TierEditor
                tiers={cpaConfig?.playerRestrictions || []}
                setTiers={(t) => setCpaConfig({ ...cpaConfig, playerRestrictions: t })}
                fields={[
                  { key: 'minDeposit', label: 'Min Deposit' },
                  { key: 'maxDeposit', label: 'Max Deposit' },
                  { key: 'cpaCommission', label: 'CPA Commission' },
                ]}
                emptyTemplate={{ minDeposit: '', maxDeposit: '', cpaCommission: '' }}
              />
            </div>
          )}
        </div>
      </Section>

      {/* Outreach */}
      <Section icon={Mail} title="Outreach" subtitle="Auto-search affiliates or paste your own list">
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => setSourceMode('auto')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
              sourceMode === 'auto' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300'
            }`}
          >
            Auto-Search Affiliates
          </button>
          <button
            type="button"
            onClick={() => setSourceMode('manual')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
              sourceMode === 'manual' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300'
            }`}
          >
            Manual Email List
          </button>
        </div>

        {sourceMode === 'auto' ? (
          <Field label="How many affiliates to find" required>
            <input type="number" min="1" max="100" className={inputCls} value={affiliateCount} onChange={e => setAffiliateCount(e.target.value)} />
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Pulled from Untapped Opportunity for the selected brand + verticals.</p>
          </Field>
        ) : (
          <div>
            <Field label="Add affiliate (email + website)">
              <div className="flex gap-2">
                <input
                  className={inputCls}
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEntry(); } }}
                  placeholder="name@site.com"
                />
                <input
                  className={inputCls}
                  value={domainInput}
                  onChange={e => setDomainInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEntry(); } }}
                  placeholder="site.com (optional)"
                />
                <button type="button" onClick={addEntry} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center gap-1">
                  <Plus size={14} /> Add
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Website is optional but improves the outreach email — without it the AI won't reference a site.</p>
            </Field>
            {manualEntries.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {manualEntries.map(e => (
                  <span key={e.email} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-slate-800 text-xs text-gray-700 dark:text-slate-300">
                    {e.email}{e.domain ? ` — ${e.domain}` : ''}
                    <button onClick={() => removeEntry(e.email)} className="hover:text-red-500"><X size={12} /></button>
                  </span>
                ))}
                <span className="text-xs text-gray-500 dark:text-slate-400 self-center ml-1">{manualEntries.length} entry{manualEntries.length === 1 ? '' : 'ies'}</span>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Submit */}
      <div className="flex items-center justify-end gap-3 mt-6">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-700 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800">
          Cancel
        </button>
        <button onClick={submit} disabled={saving} className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50">
          {saving && <Loader2 size={14} className="animate-spin" />}
          {existing ? 'Save Changes' : 'Create Agent'}
        </button>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Generic tier editor — renders rows of fields with add/remove
// ────────────────────────────────────────────────────────────────────
const TierEditor = ({ tiers, setTiers, fields, emptyTemplate }) => {
  const inputCls = 'w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500';
  const update = (i, k, v) => setTiers(tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  const remove = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  const add = () => setTiers([...(tiers || []), { ...emptyTemplate }]);

  return (
    <div>
      <div className="space-y-2">
        {(tiers || []).map((t, i) => (
          <div key={i} className="flex items-end gap-2 p-2 rounded-lg bg-gray-50 dark:bg-slate-800/50">
            {fields.map(f => (
              <div key={f.key} className="flex-1">
                <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-0.5">{f.label}</label>
                <input type="number" className={inputCls} value={t[f.key] ?? ''} onChange={e => update(i, f.key, e.target.value)} />
              </div>
            ))}
            <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-2 text-xs px-3 py-1.5 rounded-lg border border-dashed border-gray-300 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:border-emerald-500 hover:text-emerald-600 flex items-center gap-1">
        <Plus size={12} /> Add tier
      </button>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Run modal — asks for country (verticals are saved on agent)
// ────────────────────────────────────────────────────────────────────
const RunModal = ({ agent, onClose, onDone }) => {
  const [country, setCountry] = useState('');
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [senderEmail, setSenderEmail] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [smtpLoading, setSmtpLoading] = useState(true);
  const [smtpError, setSmtpError] = useState('');

  // Load connected SMTP accounts so user can pick which mailbox to send from.
  // Default order: agent's saved sender_email > primary SMTP > first SMTP.
  const loadSmtp = useCallback(async () => {
    setSmtpLoading(true);
    setSmtpError('');
    try {
      const r = await fetch(`${API}/api/senderEmail/list`, { credentials: 'include', headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'failed to load email accounts');
      const smtp = (d.data || []).filter(a => a.source === 'smtp');
      setSmtpAccounts(smtp);
      if (agent.sender_email && smtp.some(a => a.email === agent.sender_email)) {
        setSenderEmail(agent.sender_email);
      } else {
        const primary = smtp.find(a => a.is_primary) || smtp[0];
        if (primary) setSenderEmail(primary.email);
      }
    } catch (err) {
      setSmtpError(err.message || 'failed to load email accounts');
    } finally {
      setSmtpLoading(false);
    }
  }, [agent.sender_email]);

  useEffect(() => { loadSmtp(); }, [loadSmtp]);

  const run = async () => {
    setError('');
    if (!country) return setError('Pick a country.');
    if (smtpAccounts.length > 0 && !senderEmail) return setError('Pick which email to send from.');
    setRunning(true);
    try {
      const r = await fetch(`${API}/api/affiliate-agents/${agent.id}/run`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ country, sender_email: senderEmail || null }),
      });
      const data = await r.json();
      if (!data.success) throw new Error(data.message || 'Run failed');
      setResult(data);
      onDone && onDone(data);
    } catch (err) { setError(err.message); }
    finally { setRunning(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Run "{agent.name}"</h2>
          <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {result ? (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-sm">
              ✓ {result.message || `Drafted outreach for ${result.affiliateCount} affiliates.`}
            </div>
            <div className="text-xs text-gray-500 dark:text-slate-400">
              Campaign #{result.campaignId} — {result.conversations?.length || 0} conversations created. Review them in the Affiliate Agent conversations view.
            </div>
            <button onClick={onClose} className="w-full py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">Pick a country to target. Verticals come from the saved agent ({agent.verticals?.join(', ')}).</p>

            <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">Country</label>
            <select className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-slate-100 mb-3"
              value={country} onChange={e => setCountry(e.target.value)}>
              <option value="">— select country —</option>
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">Send from</label>
            {smtpLoading ? (
              <div className="mb-3 px-3 py-2 text-xs text-gray-500 dark:text-slate-400 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Loading email accounts…
              </div>
            ) : smtpError ? (
              <div className="mb-3 p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs flex items-center justify-between gap-2">
                <span>Couldn't load email accounts: {smtpError}</span>
                <button type="button" onClick={loadSmtp} className="px-2 py-1 rounded bg-red-100 dark:bg-red-900/40 text-xs">Retry</button>
              </div>
            ) : smtpAccounts.length === 0 ? (
              <div className="mb-3 p-2 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs flex items-center justify-between gap-2">
                <span>No SMTP account connected. Add one in <a href="/email-settings" className="underline">Email Settings</a>.</span>
                <button type="button" onClick={loadSmtp} className="px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/40 text-xs">Refresh</button>
              </div>
            ) : (
              <select className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-slate-100 mb-3"
                value={senderEmail} onChange={e => setSenderEmail(e.target.value)}>
                {smtpAccounts.map(a => (
                  <option key={a.id} value={a.email}>
                    {a.email}{a.is_primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            )}

            {error && <div className="mb-3 p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-600 text-xs">{error}</div>}
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-300 dark:border-slate-700 text-sm">Cancel</button>
              <button onClick={run} disabled={running} className="flex-1 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                Run Agent
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Single approval card with countdown timer + 4 actions
// ────────────────────────────────────────────────────────────────────
const ApprovalCard = ({ item, onAction, onEditingChange }) => {
  const [now, setNow] = useState(Date.now());
  const [editing, setEditing] = useState(false);
  const [editedBody, setEditedBody] = useState(item.content || '');
  const [editedSubject, setEditedSubject] = useState((item.metadata && (typeof item.metadata === 'string' ? safeParse(item.metadata) : item.metadata) || {}).subject || '');
  const [hint, setHint] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Tell the parent to pause polling while this card has unsaved input —
  // either the editor is open or the user is mid-way through typing a hint.
  useEffect(() => {
    const dirty = editing || hint.trim().length > 0;
    onEditingChange && onEditingChange(item.id, dirty);
    return () => onEditingChange && onEditingChange(item.id, false);
  }, [editing, hint, item.id, onEditingChange]);

  const sendAtMs = new Date(item.send_at).getTime();
  const remaining = Math.max(0, sendAtMs - now);
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000).toString().padStart(2, '0');
  const overdue = remaining === 0;

  const meta = (item.metadata && typeof item.metadata === 'string') ? safeParse(item.metadata) : (item.metadata || {});
  const subject = meta.subject || '(no subject)';
  const internal = meta.internal_notes;

  const act = async (kind, body) => {
    setBusy(true);
    try {
      let url, opts = { method: 'POST', credentials: 'include', headers: authHeaders() };
      if (kind === 'approve') url = `${API}/api/affiliate-agents/approvals/${item.id}/approve`;
      else if (kind === 'edit') { url = `${API}/api/affiliate-agents/approvals/${item.id}/edit-send`; opts.body = JSON.stringify(body); }
      else if (kind === 'regen') { url = `${API}/api/affiliate-agents/approvals/${item.id}/regenerate`; opts.body = JSON.stringify(body); }
      else if (kind === 'discard') { url = `${API}/api/affiliate-agents/approvals/${item.id}`; opts.method = 'DELETE'; }
      const r = await fetch(url, opts);
      const data = await r.json();
      if (!data.success) throw new Error(data.message || 'Action failed');
      onAction && onAction(kind, data);
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className={`bg-white dark:bg-slate-900 rounded-2xl border ${overdue ? 'border-red-200 dark:border-red-900/40' : 'border-gray-200 dark:border-slate-800'} p-4 mb-4 shadow-sm`}>
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900 dark:text-slate-100 truncate">{item.affiliate_email || item.affiliate_url}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">{item.brand_name}</span>
          </div>
          <div className="text-xs text-gray-500 dark:text-slate-400">
            {item.country} · {item.vertical} · subject: <span className="text-gray-700 dark:text-slate-300">{subject}</span>
          </div>
        </div>
        <div className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full ${
          overdue ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            : mins < 1 ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
            : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400'
        }`}>
          <Clock size={12} /> {overdue ? 'Auto-sending…' : `${mins}:${secs} until auto-send`}
        </div>
      </div>

      {internal && (
        <div className="mb-2 px-3 py-1.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs">
          <strong>AI note:</strong> {internal}
        </div>
      )}

      {editing ? (
        <div className="space-y-2 mb-3">
          <input value={editedSubject} onChange={e => setEditedSubject(e.target.value)} className={INPUT_CLS} placeholder="Subject" />
          <textarea value={editedBody} onChange={e => setEditedBody(e.target.value)} rows={8} className={INPUT_CLS} />
        </div>
      ) : (
        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 dark:text-slate-200 bg-gray-50 dark:bg-slate-800/50 rounded-lg p-3 mb-3 max-h-56 overflow-y-auto">{item.content}</pre>
      )}

      <div className="flex items-center gap-2 mb-2">
        <input value={hint} onChange={e => setHint(e.target.value)} placeholder='Hint to regenerate, e.g. "offer 32% for first month"' className={INPUT_CLS + ' flex-1'} />
        <button disabled={busy || !hint.trim()} onClick={() => act('regen', { hint: hint.trim() })} title="Hint & Regenerate"
          className="px-3 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1 whitespace-nowrap">
          <RotateCcw size={14} /> Regenerate
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {editing ? (
          <>
            <button disabled={busy} onClick={() => act('edit', { content: editedBody, subject: editedSubject })}
              className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
              <Send size={14} /> Send Edited
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-700 text-sm">Cancel Edit</button>
          </>
        ) : (
          <>
            <button disabled={busy} onClick={() => act('approve')}
              className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
              <Check size={14} /> Approve & Send
            </button>
            <button onClick={() => setEditing(true)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-700 text-sm flex items-center gap-1">
              <Edit3 size={14} /> Edit
            </button>
          </>
        )}
        <button disabled={busy} onClick={() => act('discard')}
          className="ml-auto px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm flex items-center gap-1">
          <Trash2 size={14} /> Discard
        </button>
      </div>
    </div>
  );
};

function safeParse(s) { try { return JSON.parse(s); } catch { return {}; } }

// ────────────────────────────────────────────────────────────────────
// Approvals view — pending AI drafts awaiting human approval
// ────────────────────────────────────────────────────────────────────
const ApprovalsView = ({ onBack }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Track which cards have unsaved edits in flight. While any card is being
  // edited we pause background polling so refreshes can't yank state from
  // under the user. Cards register/unregister themselves via these refs.
  const editingIdsRef = useRef(new Set());

  const refresh = useCallback(async ({ background = false } = {}) => {
    // First load shows the spinner; background polls update silently so
    // ApprovalCard instances don't unmount (which would blow away in-progress
    // edits and the hint input).
    if (!background) setLoading(true);
    try {
      const r = await fetch(`${API}/api/affiliate-agents/approvals`, { credentials: 'include', headers: authHeaders() });
      const d = await r.json();
      if (d.success) setItems(d.items || []);
    } finally { if (!background) setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(() => {
      // Skip the poll if any card is currently being edited — typing in an
      // edit/hint field while a refresh lands used to wipe what the user
      // typed because we'd swap items beneath them.
      if (editingIdsRef.current.size === 0) refresh({ background: true });
    }, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  const setEditing = useCallback((id, isEditing) => {
    if (isEditing) editingIdsRef.current.add(id);
    else editingIdsRef.current.delete(id);
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-400">
          <ChevronLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Inbox size={22} className="text-emerald-600" /> Approval Queue
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">AI-drafted replies awaiting your review. Auto-sent after 5 minutes.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-slate-400"><Loader2 size={20} className="animate-spin inline" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900">
          <Check size={32} className="mx-auto text-emerald-500 mb-3" />
          <h3 className="font-medium text-gray-900 dark:text-slate-100 mb-1">All clear</h3>
          <p className="text-sm text-gray-500 dark:text-slate-400">No drafts waiting for approval right now.</p>
        </div>
      ) : (
        <div>
          {items.map(it => <ApprovalCard key={it.id} item={it} onAction={() => refresh()} onEditingChange={setEditing} />)}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Main — list view + form view + approvals view
// ────────────────────────────────────────────────────────────────────
const AffiliateAgent = () => {
  const [agents, setAgents] = useState([]);
  const [brands, setBrands] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'form' | 'approvals'
  const [editing, setEditing] = useState(null);
  const [runFor, setRunFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsRes, brandsRes, approvalsRes] = await Promise.all([
        fetch(`${API}/api/affiliate-agents`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API}/api/affiliate-agents/brands`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API}/api/affiliate-agents/approvals`, { credentials: 'include', headers: authHeaders() }),
      ]);
      const agentsData = await agentsRes.json();
      const brandsData = await brandsRes.json();
      const approvalsData = await approvalsRes.json();
      if (agentsData.success) setAgents(agentsData.agents || []);
      if (brandsData.success) setBrands(brandsData.brands || []);
      if (approvalsData.success) setPendingCount((approvalsData.items || []).length);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Poll pending count every 30s while on the list view
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const openCreate = () => { setEditing(null); setView('form'); };
  const openEdit = async (agent) => {
    // Need full agent (config jsonb) — list endpoint omits some fields
    const r = await fetch(`${API}/api/affiliate-agents/${agent.id}`, { credentials: 'include', headers: authHeaders() });
    const data = await r.json();
    if (data.success) { setEditing(data.agent); setView('form'); }
  };
  const handleSaved = () => { setView('list'); setEditing(null); refresh(); };
  const handleDelete = async (agent) => {
    if (!window.confirm(`Delete "${agent.name}"?`)) return;
    await fetch(`${API}/api/affiliate-agents/${agent.id}`, { method: 'DELETE', credentials: 'include', headers: authHeaders() });
    refresh();
  };

  if (view === 'form') {
    return <AgentForm existing={editing} brands={brands} onCancel={() => { setView('list'); setEditing(null); }} onSaved={handleSaved} />;
  }
  if (view === 'approvals') {
    return <ApprovalsView onBack={() => { setView('list'); refresh(); }} />;
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Bot size={22} className="text-emerald-600" /> Affiliate Agents
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Saved agent profiles with rev/CPA terms — run them anytime against any country.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView('approvals')}
            className="relative px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-700 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center gap-2">
            <Inbox size={16} /> Approvals
            {pendingCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {pendingCount}
              </span>
            )}
          </button>
          <button onClick={openCreate} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center gap-2">
            <Plus size={16} /> New Agent
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-600 text-sm">{error}</div>}

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-slate-400"><Loader2 size={20} className="animate-spin inline" /> Loading...</div>
      ) : agents.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-gray-300 dark:border-slate-700">
          <Bot size={32} className="mx-auto text-gray-400 mb-3" />
          <h3 className="font-medium text-gray-900 dark:text-slate-100 mb-1">No agents yet</h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">Create one to start automating affiliate outreach with negotiation.</p>
          <button onClick={openCreate} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 inline-flex items-center gap-2">
            <Plus size={16} /> New Agent
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {agents.map(a => (
            <div key={a.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 p-4 flex items-center justify-between hover:shadow-md transition">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900 dark:text-slate-100 truncate">{a.name}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400">{a.model}</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>Brand: <strong className="text-gray-700 dark:text-slate-300">{a.brand_name}</strong></span>
                  {a.currency && <span>Currency: <strong className="text-gray-700 dark:text-slate-300">{a.currency}</strong></span>}
                  {a.sender_email && <span title="Send/receive email">📧 <strong className="text-gray-700 dark:text-slate-300">{a.sender_email}</strong></span>}
                  <span>Verticals: <strong className="text-gray-700 dark:text-slate-300">{(a.verticals || []).join(', ')}</strong></span>
                  {a.rev_share_type && <span className="text-emerald-600 dark:text-emerald-400">RevShare: {a.rev_share_type}</span>}
                  {a.cpa_type && <span className="text-blue-600 dark:text-blue-400">CPA: {a.cpa_type}</span>}
                  <span>Source: {a.source_mode === 'auto' ? `auto (${a.affiliate_count})` : `manual (${a.manual_email_count} emails)`}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setRunFor(a)} title="Run" className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 hover:bg-emerald-100">
                  <Play size={16} />
                </button>
                <button onClick={() => openEdit(a)} title="Edit" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800">
                  <Edit3 size={16} />
                </button>
                <button onClick={() => handleDelete(a)} title="Delete" className="p-2 rounded-lg text-gray-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {runFor && <RunModal agent={runFor} onClose={() => setRunFor(null)} onDone={() => {}} />}
    </div>
  );
};

export default AffiliateAgent;
export { ApprovalsView };
